import React from 'react';
import { Box, Typography, Container, Chip, useMediaQuery, useTheme as useMuiTheme } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import SEO from '../components/SEO';
import Header from '../components/Header';
import { getArticleBySlug } from '../data/articles';
import { getStoryById } from '../data';

const Article: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  
  const article = slug ? getArticleBySlug(slug) : undefined;

  const colors = {
    bg: isDark ? '#0f0f0f' : '#f5f5f5',
    cardBg: isDark ? '#1a1a1a' : '#ffffff',
    text: isDark ? '#e0e0e0' : '#1a1a1a',
    textSecondary: isDark ? '#999' : '#666',
    border: isDark ? '#2a2a2a' : '#e0e0e0',
    hover: isDark ? '#252525' : '#f8f8f8',
    accent: isDark ? '#C084FC' : '#9333EA',
  };

  if (!article) {
    return (
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: colors.bg
      }}>
        <Header isMobile={isMobile} />
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          <Typography variant="h4" sx={{ color: colors.text }}>
            Article not found
          </Typography>
        </Box>
      </Box>
    );
  }

  // Article structured data for SEO
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.imageUrl || "https://dailystories.fr/podcast.png",
    "author": {
      "@type": "Person",
      "name": article.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Daily Stories",
      "logo": {
        "@type": "ImageObject",
        "url": "https://dailystories.fr/podcast.png"
      }
    },
    "datePublished": article.publishDate,
    "dateModified": article.publishDate,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://dailystories.fr/articles/${article.slug}`
    },
    "keywords": article.keywords.join(', ')
  };

  // Breadcrumb structured data
  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://dailystories.fr"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Articles",
        "item": "https://dailystories.fr/articles"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": article.title,
        "item": `https://dailystories.fr/articles/${article.slug}`
      }
    ]
  };

  return (
    <>
      <SEO 
        title={`${article.title} | Daily Stories`}
        description={article.excerpt}
        keywords={article.keywords.join(', ')}
        url={`https://dailystories.fr/articles/${article.slug}`}
        image={article.imageUrl}
      />
      
      {/* Article Schema */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleStructuredData) }}
      />
      
      {/* Breadcrumb Schema */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbStructuredData) }}
      />
      
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: colors.bg
      }}>
        <Header isMobile={isMobile} />

        <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 }, px: { xs: 3, md: 4 } }}>
          {/* Hero Image */}
          {article.imageUrl && (
            <Box 
              sx={{ 
                mb: 6,
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: isDark 
                  ? '0 4px 12px rgba(0,0,0,0.3)' 
                  : '0 4px 12px rgba(0,0,0,0.1)',
              }}
            >
              <Box
                component="img"
                src={article.imageUrl}
                alt={article.imageAlt || article.title}
                sx={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  maxHeight: '500px',
                  objectFit: 'cover'
                }}
              />
            </Box>
          )}

          {/* Article Header */}
          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h3" 
              sx={{ 
                color: colors.text,
                fontWeight: 600,
                mb: 3,
                fontSize: { xs: '1.75rem', md: '2.5rem' },
                lineHeight: 1.2
              }}
            >
              {article.title}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                By {article.author}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                •
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                {article.readTime}
              </Typography>
            </Box>

            <Typography 
              variant="h6" 
              sx={{ 
                color: colors.textSecondary,
                fontWeight: 400,
                fontStyle: 'italic',
                mb: 4
              }}
            >
              {article.excerpt}
            </Typography>
          </Box>

          {/* Article Content */}
          <Box 
            sx={{ 
              color: colors.text,
              '& p': { 
                mb: 3, 
                lineHeight: 1.8,
                fontSize: '1.1rem'
              },
              '& h2': { 
                mt: 5, 
                mb: 3, 
                fontWeight: 600,
                fontSize: '1.75rem'
              },
              '& h3': { 
                mt: 4, 
                mb: 2, 
                fontWeight: 600,
                fontSize: '1.4rem'
              },
              '& ul, & ol': {
                mb: 3,
                pl: 4,
                '& li': {
                  mb: 1,
                  lineHeight: 1.8
                }
              },
              '& strong': {
                fontWeight: 600,
                color: colors.text
              },
              '& a': {
                color: colors.accent,
                textDecoration: 'none',
                fontWeight: 500,
                '&:hover': {
                  textDecoration: 'underline'
                }
              }
            }}
          >
            {article.content.split('\n\n').map((paragraph, index) => {
              if (paragraph.startsWith('## ')) {
                return (
                  <Typography key={index} variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600, color: colors.text }}>
                    {paragraph.replace('## ', '')}
                  </Typography>
                );
              } else if (paragraph.startsWith('### ')) {
                return (
                  <Typography key={index} variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600, color: colors.text }}>
                    {paragraph.replace('### ', '')}
                  </Typography>
                );
              } else if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                return (
                  <Typography key={index} sx={{ mb: 3, lineHeight: 1.8, fontSize: '1.1rem', fontWeight: 600, color: colors.text }}>
                    {paragraph.replace(/\*\*/g, '')}
                  </Typography>
                );
              } else {
                // Parse markdown-style links [text](/story/id) and bold **text**
                const parts: React.ReactNode[] = [];
                let lastIndex = 0;
                
                // Match both links and bold text
                const regex = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*/g;
                let match: RegExpExecArray | null = null;
                
                while ((match = regex.exec(paragraph)) !== null) {
                  const currentMatch = match; // Capture for closure
                  
                  // Add text before match
                  if (currentMatch.index > lastIndex) {
                    parts.push(paragraph.substring(lastIndex, currentMatch.index));
                  }
                  
                  if (currentMatch[1] && currentMatch[2]) {
                    // It's a link [text](url)
                    const linkText = currentMatch[1];
                    const linkUrl = currentMatch[2];
                    parts.push(
                      <Box
                        key={`link-${index}-${currentMatch.index}`}
                        component="span"
                        onClick={() => navigate(linkUrl)}
                        sx={{
                          color: colors.accent,
                          cursor: 'pointer',
                          fontWeight: 500,
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        {linkText}
                      </Box>
                    );
                  } else if (currentMatch[3]) {
                    // It's bold **text**
                    parts.push(
                      <Box key={`bold-${index}-${currentMatch.index}`} component="strong" sx={{ fontWeight: 600 }}>
                        {currentMatch[3]}
                      </Box>
                    );
                  }
                  
                  lastIndex = currentMatch.index + currentMatch[0].length;
                }
                
                // Add remaining text
                if (lastIndex < paragraph.length) {
                  parts.push(paragraph.substring(lastIndex));
                }
                
                return (
                  <Typography key={index} sx={{ mb: 3, lineHeight: 1.8, fontSize: '1.1rem', color: colors.text }}>
                    {parts.length > 0 ? parts : paragraph}
                  </Typography>
                );
              }
            })}
          </Box>

          {/* Featured Stories */}
          {article.featuredStories && article.featuredStories.length > 0 && (
            <Box sx={{ mt: 8, pt: 6, borderTop: `1px solid ${colors.border}` }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  color: colors.text,
                  fontWeight: 600,
                  mb: 4
                }}
              >
                Featured Stories
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {article.featuredStories.slice(0, 5).map((storyId) => {
                  const story = getStoryById(storyId);
                  if (!story) return null;
                  
                  return (
                    <Box
                      key={story.id}
                      onClick={() => navigate(`/story/${story.id}`)}
                      sx={{
                        p: 3,
                        backgroundColor: colors.cardBg,
                        border: `1px solid ${colors.border}`,
                        borderRadius: 1,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: colors.hover,
                          transform: 'translateX(4px)',
                        }
                      }}
                    >
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: colors.text,
                          fontWeight: 600,
                          mb: 1
                        }}
                      >
                        {story.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: colors.textSecondary,
                          mb: 2
                        }}
                      >
                        {story.excerpt}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip 
                          label={story.city} 
                          size="small"
                          sx={{ 
                            backgroundColor: colors.hover,
                            color: colors.text,
                            fontSize: '0.75rem'
                          }}
                        />
                        <Chip 
                          label={story.readTime} 
                          size="small"
                          sx={{ 
                            backgroundColor: colors.hover,
                            color: colors.text,
                            fontSize: '0.75rem'
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Container>
      </Box>
    </>
  );
};

export default Article;
